import { useQuery } from "@apollo/client"
import { ControlGroup } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomRadio from "components/Atoms/CustomRadio"
import CustomRadioGroup from "components/Atoms/CustomRadioGroup"
import Divider from "components/Atoms/Divider"
import StaticMultiSelector from "components/Atoms/StaticMultiSelector"
import StaticSelector from "components/Atoms/StaticSelector"
import StringInput from "components/Atoms/StringInput"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import FormController from "components/Molecules/FormController"
import FileUploadDialog from "components/Templates/FileUploadDialog"
import GoogleMapDialog from "components/Templates/GoogleMapDialog"
import UserSelector from "components/Templates/selectors/UserSelector"
import { ALLCPC, ALL_DEPARTMENTS, GROUPS } from "graphql/selector.queries.gql"
import useSearchParams from "hooks/useSearchParams"
import React, { useState } from "react"
import { useParams } from "react-router-dom"
import classNames from "utils/classNames"
import styles from "../dc.module.scss"
import useDCStore from "../dc.store"
import FormLayout from "../FormLayout"
import useDCRemoveM2M from "../useDCRemoveM2M"
import DCPeopleResponsible from "./DCPeopleResponsible"
const DCBasicDetails = () => {
  const [search] = useSearchParams()
  const isCpc = search?.type === "cpc"
  const { id } = useParams<any>()

  const [isFileUploadOpen, setUpload] = useState(false)

  const { data: allDcs } = useQuery(ALLCPC)
  const { data: groups } = useQuery(GROUPS, {
    variables: {
      category: "DCT",
    },
  })

  const [basicDetails, errors, setBasicDetails, disabled] = useDCStore(
    store => [
      store.state.basicDetails,
      store.errors.basicDetails?.details,
      store.setData("basicDetails"),
      store.disabled,
    ]
  )
  const { removeLink } = useDCRemoveM2M()
  return (
    <FormController
      errors={errors}
      disabled={disabled}
      disableAll
      source={basicDetails}
      onChange={setBasicDetails}
    >
      <FormLayout title="Basic Details">
        <div className={styles.grid}>
          <StringInput required path="name" label="Name" big={false} />

          <CustomFormGroup label="DC Address">
            <GoogleMapDialog
              btnProps={{
                fill: true,
                big: false,
                text: "Select Address",
              }}
              path="address"
            />
          </CustomFormGroup>
          <CustomFormGroup
            className="vt-horizontal-radio-group"
            label="Location is a manufacturing plant"
          >
            <CustomRadioGroup path="isManufacturingPlant">
              <CustomRadio name="Yes" id="yes" />
              <CustomRadio name="No" id="no" />
            </CustomRadioGroup>
          </CustomFormGroup>
          <CustomFormGroup
            required
            label="Codename"
            helperText="eg. 3000 4567 SF4567"
            className={styles["codename"]}
          >
            <ControlGroup fill={true}>
              <StringInput
                className="mb-0"
                big={false}
                path="companyCode"
                placeholder="Company"
              />
              <StringInput
                className="mb-0"
                big={false}
                path="plantCode"
                placeholder="Plant"
              />
              <StringInput
                className="mb-0"
                big={false}
                path="storeCode"
                placeholder="Store"
              />
              <StringInput
                className="mb-0"
                big={false}
                path="salesOfcCode"
                placeholder="Sales Office"
              />
              <StringInput
                className="mb-0"
                big={false}
                path="partnerCode"
                placeholder="Partner"
              />
              <StringInput
                className="mb-0"
                big={false}
                path="customerCode"
                placeholder="Customer"
              />
            </ControlGroup>
          </CustomFormGroup>
          {!isCpc ? (
            <StaticSelector
              nullable
              label="Assigned CPC"
              path="assignedCpc"
              items={
                id
                  ? allDcs?.allDistributionCentres.filter(
                      (dc: any) => dc.id !== id
                    )
                  : allDcs?.allDistributionCentres
              }
            />
          ) : (
            <div />
          )}
          <DynamicMultiSelector
            fill
            query={ALL_DEPARTMENTS}
            label="Departments"
            path="allowedDepartments"
            renderTag={item => item?.name}
            renderText={item => item?.name}
            searchBy={["name"]}
            onDelete={department =>
              removeLink({
                fieldName: "allowed_departments",
                fieldIds: [department?.id],
                displayName: "Departments",
              })
            }
          />
          <CustomButton
            className="self-end"
            disabled={disabled}
            icon="cloud-upload"
            text="Upload Files"
            onClick={e => setUpload(true)}
          />

          <FileUploadDialog
            onDelete={photo =>
              removeLink({
                fieldName: "photos",
                fieldIds: [photo?.id],
                displayName: "Photo",
              })
            }
            btnClassName="self-end"
            path="photos"
            isOpen={isFileUploadOpen}
            onClose={e => setUpload(false)}
          />
        </div>
      </FormLayout>
      <Divider className="mb-0" />
      <FormLayout
        rightElement={
          <UserSelector
            inline
            nullable
            className="mb-0"
            label="Primary Contact"
            group="*"
            path="primaryPoc"
          />
        }
        title="People"
        subtitle="The people entered here will get the access to this DC"
      >
        <div className={classNames(styles.grid, styles.grid_cols_2)}>
          <StaticMultiSelector
            fill={true}
            big={false}
            label="User groups"
            path="groupVisibility"
            items={groups?.allGroups ?? []}
            onDelete={group =>
              removeLink({
                fieldName: "groupVisibility",
                fieldIds: [group?.id],
                displayName: "User Group",
              })
            }
          />
          <DCPeopleResponsible
            onDelete={user =>
              removeLink({
                fieldName: "pointOfContacts",
                fieldIds: [user?.id],
                displayName: "Point Of Contact",
              })
            }
          />
        </div>
      </FormLayout>
    </FormController>
  )
}

export default DCBasicDetails
